import { spacing } from '@carvana/ds-styles';
import cx from 'classnames';
import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import BackToTopButton from '@/vdp/DesktopPageNavigation/BackToTopButton';
import NavigationLink from '@/vdp/DesktopPageNavigation/NavigationLink';
import { SectionId, type TSectionId } from '~/constants/vdp/section';
import { useIsLargeDesktop } from '~/context/common/DeviceContext';
import NavigationStickyContext from '~/context/vdp/NavigationStickyContext';
import StarRatingOwnerReviewContext from '~/context/vdp/StarRatingOwnerReviewContext';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useShouldDisplayOrderStatus from '~/hooks/vdp/selectors/useShouldDisplayOrderStatus';
import useShouldShowPriceDetails from '~/hooks/vdp/selectors/useShouldShowPriceDetails';
import useShowInspection from '~/hooks/vdp/selectors/useShowInspection';
import useIsInInspectionExperiment from '~/hooks/vdp/useIsInInspectionExperiment';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import {
  DesktopStickyPageNavigationInnerWrapper,
  DesktopStickyPageNavigationWrapper
} from './DesktopPageNavigation.styles';
import { useCurrentSection } from './hooks';

interface TabConfig {
  id: TSectionId;
  label: string;
  displayText: ReactNode;
  shouldShow?: boolean;
}

interface TabProps {
  analyticsId: string;
  isActive: boolean;
  config: TabConfig;
}

function Tab({ analyticsId, isActive, config }: TabProps) {
  return (
    <NavigationLink
      aria-current={isActive ? 'location' : undefined}
      className={cx(
        'h-fit py-lg font-semibold transition-all duration-300 hover:text-text-strong focus:bg-background-primary-subtle focus:text-text-primary',
        isActive
          ? 'bg-background-primary-subtle text-text-primary hover:bg-background-primary-subtle'
          : 'text-text-strong'
      )}
      data-adobe-id={null}
      data-analytics-id={analyticsId}
      data-analytics-attributes={JSON.stringify({
        label: config.label
      })}
      data-testid="navigation-tab"
      href={`#${config.id}`}
    >
      <p className="">{config.displayText}</p>
    </NavigationLink>
  );
}

// CarvanaDS spacing values stored as pixel values for calculating height
const spacingXl = 16;
const spacing4xl = 40;
const spacing5xl = 48;
const spacing7xl = 64;

function DesktopPageNavigation() {
  const { isNavigationSticky } = useContext(NavigationStickyContext);
  const { hasOwnerReviews } = useContext(StarRatingOwnerReviewContext);
  const isLargeDesktop = useIsLargeDesktop();
  const navigationRef = useRef<HTMLDivElement>(null);
  const shouldDisplayOrderStatus = useShouldDisplayOrderStatus();
  const shouldShowPriceDetails = useShouldShowPriceDetails();
  const showInspection = useShowInspection();
  const isInInspectionExperiment = useIsInInspectionExperiment();
  const isNotAvailableForImmediatePurchase =
    useIsNotAvailableForImmediatePurchase();
  const analyticsId = isNavigationSticky ? 'Sticky Header' : 'Navbar';
  const displayPriceDetailsNav =
    shouldShowPriceDetails && !shouldDisplayOrderStatus;

  // Show owner reviews tab on initial load
  const showOwnerReviews = hasOwnerReviews.data || !hasOwnerReviews.isLoaded;

  // Track navigation top offset for scroll-based section detection
  const [currentSectionTopOffset, setCurrentSectionTopOffset] = useState(0);

  const navigationConfig: TabConfig[] = useMemo(
    () => [
      {
        id: SectionId.WhyBuy,
        label: 'Why Buy',
        displayText: 'Overview',
        shouldShow: true
      },
      {
        id: SectionId.FeaturesAndHighlights,
        label: 'Key Features',
        displayText: 'Features',
        shouldShow: true
      },
      {
        id: SectionId.VehicleDetails,
        label: 'Vehicle Details',
        displayText: 'Vehicle Details',
        shouldShow: true
      },
      {
        id: SectionId.PriceDetails,
        label: 'Price Details',
        displayText: 'Pricing',
        shouldShow: displayPriceDetailsNav
      },
      {
        id: SectionId.OwnerReviews,
        label: 'Owner Reviews',
        displayText: (
          <span className="flex gap-sm">
            <span className="hidden lg:block">Owner</span>
            <span>Reviews</span>
          </span>
        ),
        shouldShow: showOwnerReviews
      },
      {
        id: isInInspectionExperiment
          ? SectionId.InspectionAndGuarantee
          : SectionId.Inspection,
        label: 'Inspection',
        displayText: 'Inspection',
        shouldShow: showInspection
      },
      {
        id: SectionId.RecommendedVehicles,
        label: 'Recommended Vehicles',
        displayText: 'More Cars',
        shouldShow:
          (isLargeDesktop ||
            !showInspection ||
            !hasOwnerReviews.data ||
            !displayPriceDetailsNav) &&
          !isNotAvailableForImmediatePurchase
      }
    ],
    [
      displayPriceDetailsNav,
      hasOwnerReviews.data,
      isInInspectionExperiment,
      isLargeDesktop,
      isNotAvailableForImmediatePurchase,
      showInspection,
      showOwnerReviews
    ]
  );

  const validSectionIds = useMemo(
    () =>
      navigationConfig
        .filter(config => config.shouldShow)
        .map(config => config.id),
    [navigationConfig]
  );

  const currentSection = useCurrentSection(
    validSectionIds,
    currentSectionTopOffset
  );

  useEffect(() => {
    const element = navigationRef.current;
    if (!element) return;

    const updateHeight = () => {
      const height = element.offsetHeight;
      if (height > 0) {
        const calculatedHeightWithPadding = height + spacingXl + spacing4xl;
        const breakpointSmallSectionPadding = spacing5xl;
        const breakpointMediumSectionPadding = spacing7xl;

        setCurrentSectionTopOffset(height + spacingXl);

        document.body.style.setProperty(
          '--sticky-info-card-top-sm',
          `${calculatedHeightWithPadding}px`
        );
        document.body.style.setProperty(
          '--sticky-info-card-top-md',
          spacing.xl
        );
        document.body.style.setProperty(
          '--scroll-top-sm',
          `${calculatedHeightWithPadding - breakpointSmallSectionPadding}px`
        );
        document.body.style.setProperty(
          '--scroll-top-md',
          `${calculatedHeightWithPadding - breakpointMediumSectionPadding}px`
        );
      }
    };

    // Set initial height
    updateHeight();

    // Use ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    resizeObserver.observe(element);

    // Fallback: retry after a short delay in case initial render hasn't completed
    const timeoutId = setTimeout(updateHeight, 100);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      document.body.style.removeProperty('--sticky-info-card-top-sm');
      document.body.style.removeProperty('--sticky-info-card-top-md');
      document.body.style.removeProperty('--scroll-top-sm');
      document.body.style.removeProperty('--scroll-top-md');
    };
  }, [displayPriceDetailsNav, hasOwnerReviews, showInspection, isLargeDesktop]);

  return (
    <>
      <DesktopStickyPageNavigationWrapper
        data-testid="desktop-sticky-page-navigation-wrapper"
        id="desktop-navigation"
      >
        <DesktopStickyPageNavigationInnerWrapper ref={navigationRef}>
          {navigationConfig.map(config =>
            config.shouldShow ? (
              <Tab
                analyticsId={analyticsId}
                isActive={currentSection === config.id}
                config={config}
                key={config.id}
              />
            ) : null
          )}
        </DesktopStickyPageNavigationInnerWrapper>
      </DesktopStickyPageNavigationWrapper>
      <BackToTopButton />
    </>
  );
}

export default withErrorBoundary(DesktopPageNavigation, {
  section: 'VDP DesktopPageNavigation'
});
